import { Phone, ShieldCheck, Sparkles, Scale } from "lucide-react";
import { motion } from "framer-motion";
import { CTAButton } from "@/components/shared/CTAButton";
import { FloatingReviewCard } from "@/components/shared/FloatingReviewCard";
import { Watermark } from "@/components/shared/Watermark";
import { FIRM } from "@/constants/firmInfo";
import { CaseEvaluationDialog } from "@/components/home/CaseEvaluationDialog";
import { useLanguage } from "@/i18n/LanguageContext";

export const Hero = () => {
  const { t, isSpanish } = useLanguage();

  const TRUST_BADGES = [
    { icon: ShieldCheck, label: isSpanish ? `Fundada en ${FIRM.foundedYear}` : `Est. ${FIRM.foundedYear}` },
    { icon: Scale, label: t("hero.badge2") },
    { icon: Sparkles, label: t("hero.badge3") },
  ];

  return (
    <section className="relative overflow-hidden bg-[color:var(--navy-950)]" data-testid="hero-section">
      <Watermark word="Justice" position="top-right" className="text-[color:var(--gold-500)] top-4" />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(1200px 600px at 15% 0%, rgba(184,154,61,0.16), transparent 55%), radial-gradient(900px 500px at 85% 20%, rgba(255,255,255,0.06), transparent 60%)",
        }}
      />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24 lg:py-28 grid lg:grid-cols-12 gap-12 items-center">
        <motion.div
          className="lg:col-span-7"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="inline-block text-xs font-medium tracking-[0.22em] uppercase text-[color:var(--gold-400)] mb-5">
            {t("hero.eyebrow")}
          </span>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-[4.5rem] font-medium tracking-tight text-white leading-[1.05] text-balance">
            {t("hero.titleLine1")}{" "}
            <span className="italic text-[color:var(--gold-400)]">{t("hero.titleEmphasis")}</span> {t("hero.titleLine2")}
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl">
            {t("hero.subtitle")}
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <CaseEvaluationDialog
              trigger={
                <CTAButton variant="brass" data-testid="hero-instant-case-evaluation-button">
                  {t("hero.cta")}
                </CTAButton>
              }
            />
            <a href={`tel:${FIRM.phoneTel}`} data-testid="hero-call-now-link">
              <CTAButton variant="outline" icon={Phone}>
                {FIRM.phoneDisplay}
              </CTAButton>
            </a>
          </div>

          <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3">
            {TRUST_BADGES.map((badge, idx) => (
              <div key={idx} className="flex items-center gap-2 text-sm text-slate-300" data-testid={`hero-trust-badge-${idx}`}>
                <badge.icon className="h-4 w-4 text-[color:var(--gold-500)]" />
                {badge.label}
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          className="lg:col-span-5"
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
        >
          <div className="relative rounded-[2rem] border border-white/10 p-2 bg-white/5 shadow-[0_30px_80px_rgba(0,0,0,0.4)]">
            <div className="rounded-[1.6rem] overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1686771416282-3888ddaf249b"
                alt="Attorney shaking hands with a client after a successful consultation at Rinnou & Associates"
                className="w-full h-[440px] object-cover"
                loading="eager"
              />
            </div>
            <FloatingReviewCard
              name="Debra Florence"
              quote="If you want an intelligent, caring, interested and efficient lawyer — call Rinnou & Associates."
              className="absolute -bottom-10 -left-6 sm:-bottom-12 sm:-left-12 hidden sm:block"
            />
            <div className="absolute -top-6 -right-6 h-16 w-16 rounded-full bg-[color:var(--gold-500)] text-[color:var(--navy-950)] grid place-items-center shadow-[0_10px_30px_rgba(184,154,61,0.35)] rotate-6">
              <span className="text-center leading-none">
                <span className="block text-base font-heading font-semibold">5.0</span>
                <span className="block text-[9px] tracking-wide">STARS</span>
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
